/* eslint-disable prettier/prettier */
import getDecorators from "inversify-inject-decorators";
import { SimpleGame } from "../../../app";
import container from "../../../ioc/inversify.config";
import TYPES from "../../../ioc/types";
import { AuthenticatedAssetLoader } from "../../../services/authenticated-asset-loader";
import { DefaultEventService } from "../../../services/event-service";
import { GameStateService } from "../../../services/game-state-service";
import { KeyboardMappingService } from "../../../services/keyboardmapping-service";
import {
    BattleContextParams,
    Controls,
    GameMap,
    PlayerSprite,
    Scene,
    WarpPoint,
} from "../../../shared/domain";
import { GameStateDTO, ItemDefinitionDTO, SceneDTO } from "../../../shared/dtos";
import { Endpoints, getAssetUrl, SceneId, SocketEvents, SpriteSheets } from "../../../shared/enum";
import { CameraService } from "../../ui-services/camera-service";
import { UIMapService } from "../../ui-services/ui-map-service";
import { assertMapDataPresent, findUnbackedTilesets } from "./scene-map-validation";

const { lazyInject } = getDecorators(container);

export class SceneParameters {
    spawn?: string;
}

export class AbstractMapScene extends Phaser.Scene {
    @lazyInject(TYPES.UIMapService)
    private mapService: UIMapService;

    @lazyInject(TYPES.CameraService)
    private cameraService: CameraService;

    @lazyInject(TYPES.KeyboardMappingService)
    private keyboardMappingService: KeyboardMappingService;

    @lazyInject(TYPES.GameStateService)
    private gameStateService: GameStateService;

    @lazyInject(TYPES.EventService)
    private eventService: DefaultEventService;

    @lazyInject(TYPES.AuthenticatedAssetLoader)
    private assetLoader: AuthenticatedAssetLoader;

    protected controls: Controls;

    // à typer
    private sceneParameters;

    private cursors: Phaser.Types.Input.Keyboard.CursorKeys;

    private fullyLoaded = false;

    public static SOCKET_EVENTS_BOUND = false;

    private currentScene: Scene;

    private currentMap: GameMap;

    private animationsLoaded: boolean = false;

    private playerSprite: PlayerSprite;

    private savePositionTimer: Phaser.Time.TimerEvent;

    /**
     * WARNING: this is called for EACH SCENE.
     * That means that the network bindings and all these things are performed for all the scenes
     * that are defined in the boot/game.ts, or so it seems
     * @param sceneId
     */
    constructor(private sceneId: string) {
        super({ key: sceneId });
        console.log("calling constructor of abstract scene");
    }

    public init(sceneParameters?) {
        SimpleGame.CURRENT_MAP_ID = this.sceneId;
        console.log("scene parameters: ", sceneParameters);
        this.sceneId = sceneParameters.sceneId ? sceneParameters.sceneId : this.sceneId;
        this.sceneParameters = sceneParameters;
        this.fullyLoaded = false;
        // Unbind first so re-initialising the scene (e.g. warp) replaces old handlers cleanly.
        this.unbindSocketEvents();
        this.bindSocketEvents();
        //        console.log('init scene: ', this.sceneId);
    }

    public preload(): void {
        // Main character sprite is now loaded dynamically in initAssets()
        // based on the main_character_sprite_data from GameState
    }

    public bindSocketEvents() {
        console.log("binding socket event from ", this.sceneId);
        // Old dialog events removed - new dialog system uses WebSocket events directly
        this.eventService.register(SocketEvents.OPEN_BUY_WINDOW, (args) =>
            this.onOpenBuyWindow(args)
        );
        this.eventService.register("interaction_failed", (args) => this.onInteractionFailed(args));
    }

    public unbindSocketEvents() {
        // Old dialog events removed
        this.eventService.off(SocketEvents.OPEN_BUY_WINDOW);
        this.eventService.off("interaction_failed");
    }

    public onOpenBuyWindow(args: string) {
        const items = Object.assign(new Array<ItemDefinitionDTO>(), JSON.parse(args));

        // Close dialog if active (new dialog system)
        const dialogScene = this.scene.get(SceneId.BASIC_DIALOG);
        if (dialogScene && dialogScene.scene.isActive()) {
            dialogScene.scene.sleep();
        }

        console.log("pausing scene in open buy", this.scene);
        this.scene.run(SceneId.BUY_SCENE, { callingScene: this, items: items }); //, triggeringNpc: facingNpc.dialog_event});
        this.scene.pause();
    }

    public onInteractionFailed(data: any) {
        console.log("Interaction failed:", data);

        if (data.reason === "no_target") {
            // Nothing to interact with - this is normal, no need to show anything
            // Could add a subtle "..." text or sound effect here if desired
        } else if (data.reason === "npc_error") {
            console.error("NPC interaction error:", data.message);
            // Could show error message to player
        }
    }

    /**
     * We have a 'problem' that makes us use the create() kinda like the 'preload' ;
     * we need to know the assets from the map, but we don't know if we can get the value before reaching
     * the create in Phaser (I read some stuffs about setting the loading to blocking somehow, not sure it's
     * great).
     * So we use create as a sort of second preload function that calls the "actual" scene creation when
     * the tile assets finished loading
     */
    public create(): void {
        // Load authenticated JSON assets first, then proceed
        console.log("Creating scene ", this.sceneId);
        this.loadAuthenticatedAssets()
            .then(() => {
                const scene = this.cache.json.get(this.sceneId) as SceneDTO;

                console.log("Scene ", scene);
                if (!this.cache.tilemap.get(this.sceneId)) {
                    return this.initAssets(scene);
                } else {
                    // Assets already cached, but we still need to initialize the scene
                    this.startAfterLoading();
                }
            })
            .catch((error) => {
                console.error(`Scene "${this.sceneId}" failed to load:`, error);
                this.showLoadFailure(error);
            });
    }

    /**
     * Render the failure on the canvas.
     *
     * Without this the scene simply never starts and the player is left with a black screen,
     * the only trace being a Phaser stack trace in the console.
     */
    private showLoadFailure(error: unknown): void {
        const message = error instanceof Error ? error.message : String(error);
        const { width, height } = this.scale;

        this.add.rectangle(0, 0, width, height, 0x1a0000).setOrigin(0, 0).setDepth(10000);
        this.add
            .text(width / 2, height / 2, `Could not load this scene\n\n${message}`, {
                fontFamily: "monospace",
                fontSize: "14px",
                color: "#ff9d9d",
                align: "center",
                wordWrap: { width: width - 80 },
            })
            .setOrigin(0.5, 0.5)
            .setDepth(10001);
    }

    /**
     * Load all JSON assets from authenticated endpoints.
     * These require credentials to be sent with the requests.
     */
    private async loadAuthenticatedAssets(): Promise<void> {
        const savedGameId = SimpleGame.CURRENT_GAME;
        const projectId = SimpleGame.PROJECT_ID;

        // Load all authenticated JSON assets in parallel
        await this.assetLoader.loadJsonBatch(this, [
            {
                key: "gameState",
                url: `${Endpoints.BASE_URL}/${Endpoints.GAME_STATE}/${savedGameId}`,
            },
            {
                key: "keyboardMapping",
                url: `${Endpoints.BASE_URL}/${Endpoints.PLAYER_CONFIGURATION}/keyboardMapping`,
            },
            // NPCs are now embedded in scene data, so we don't fetch them separately
            {
                key: this.sceneId,
                url: `${Endpoints.BASE_URL}/project/${projectId}/${Endpoints.SCENE}/${this.sceneId}`,
            },
        ]);
        console.log("All authenticated assets loaded for scene ", this.sceneId);
    }

    public async initAssets(scene: SceneDTO): Promise<void> {
        // Checked before anything is queued: load.tilemapTiledJSON accepts a null url and
        // only fails later inside the loader, with no mention of the scene at fault.
        assertMapDataPresent(scene, this.sceneId);

        this.currentScene = Scene.fromDTO(this.cache.json.get(this.sceneId) as SceneDTO);
        console.log("current scene in init assets: ", this.currentScene);

        const unbackedTilesets = findUnbackedTilesets(scene.tiled_map, this.currentScene.assets);
        if (unbackedTilesets.length > 0) {
            console.warn(
                `Scene "${this.sceneId}": tileset(s) ${unbackedTilesets.join(", ")} have no ` +
                    `matching entry in the scene assets, so no texture will be loaded for them. ` +
                    `Expect the layers using them to render empty.`
            );
        }

        const blobUrls: string[] = [];

        await this.loadMainCharacterSprite(blobUrls);

        this.load.atlas(
            "atlas_sheet",
            getAssetUrl("characters/atlas_test_spritesheet.png"),
            getAssetUrl("characters/atlas_test_spritesheet.json")
        );

        // Pre-fetch scene spritesheets/tilesets with credentials, swap URLs to blob URLs
        const spriteEntries = this.currentScene.getSpriteesheetsAsURL() as Phaser.Types.Loader.FileTypes.SpriteSheetFileConfig[];
        for (const entry of spriteEntries) {
            if (entry.url) {
                try {
                    const blobUrl = await this.assetLoader.fetchAsBlob(entry.url);
                    blobUrls.push(blobUrl);
                    entry.url = blobUrl;
                } catch (err) {
                    console.error(`Failed to fetch asset "${entry.key}":`, err);
                }
            }
        }
        this.load.spritesheet(spriteEntries);

        this.load.tilemapTiledJSON(this.sceneId, scene.tiled_map);
        this.load.once("complete", () => {
            blobUrls.forEach((u) => URL.revokeObjectURL(u));
            this.startAfterLoading();
        }, this);
        this.load.start();
    }

    private async loadMainCharacterSprite(blobUrls: string[]): Promise<void> {
        // Load main character sprite from game state
        const gameState = this.cache.json.get("gameState") as GameStateDTO;
        if (!gameState?.main_character_sprite_data) {
            console.warn("No main character sprite data found in game state");
            return;
        }

        const spriteData = gameState.main_character_sprite_data;
        console.log("Loading main character sprite:", spriteData);

        let imageUrl = spriteData.online_image
            ? getAssetUrl(spriteData.online_image)
            : getAssetUrl(spriteData.image_path);

        // Fetch with credentials if served through the authenticated image proxy
        if (imageUrl.includes("/api/v1/images/")) {
            try {
                const blobUrl = await this.assetLoader.fetchAsBlob(imageUrl);
                blobUrls.push(blobUrl);
                imageUrl = blobUrl;
            } catch (err) {
                console.error("Failed to fetch character sprite:", err);
            }
        }

        if (spriteData.type === "atlas") {
            const jsonPath = spriteData.image_path.replace(/\.(png|jpg)$/, ".json");
            const jsonUrl = getAssetUrl(jsonPath);
            this.load.atlas(SpriteSheets.PLAYER, imageUrl, jsonUrl);
        } else {
            this.load.spritesheet(SpriteSheets.PLAYER, imageUrl, {
                frameWidth: spriteData.width,
                frameHeight: spriteData.height,
            });
        }
    }

    /**
     * Workaround / hack to load assets in create, because the assets data cannot (?) be loaded yet
     * as it is not yet in the cache
     */
    public startAfterLoading() {
        // Guard: prevent double-initialisation (e.g. load.on firing twice, or cached-asset
        // branch calling this directly while a load.once callback is also pending).
        if (this.fullyLoaded) {
            console.warn("AbstractMapScene: startAfterLoading called while already loaded – ignoring duplicate call");
            return;
        }
        this.fullyLoaded = true;

        this.controls = this.cache.json.get("keyboardMapping");
        this.keyboardMappingService.setControls(this.controls);

        const gameState = this.cache.json.get("gameState") as GameStateDTO;

        this.gameStateService.gameState = gameState;

        this.currentMap = this.mapService.createTileMap(
            this.currentScene,
            this,
            gameState,
            this.sceneParameters.spawn
        );
        this.currentMap.loadNPCs();

        // Initialize player sprite for direction tracking
        this.playerSprite = new PlayerSprite(this.currentMap.player);

        this.setWarps();

        if (!this.animationsLoaded) {
            this.initAnims();
        }
        this.setCamera();

        // user input
        this.cursors = this.input.keyboard.createCursorKeys();

        this.bindKeyboardEvents();

        // Cancel any previously running timer before creating a new one, otherwise
        // each call to startAfterLoading() stacks another looping save-position timer.
        if (this.savePositionTimer) {
            this.savePositionTimer.remove(false);
        }
        this.savePositionTimer = this.time.addEvent({
            delay: 2000,
            callback: () => this.gameStateService.saveCharacterPosition(),
            callbackScope: this,
            loop: true,
        });

        //@ts-ignore
        this.physics.add.overlap(
            this.currentMap.player,
            this.currentMap.encounters,
            this.onMeetEnemy,
            //@ts-ignore
            false,
            this
        );
        // Register a persistent wake handler (off-before-on prevents duplicate registrations
        // if startAfterLoading were somehow called again despite the guard above).
        this.sys.events.off("wake", this.wake, this);
        this.sys.events.on("wake", this.wake, this);

        // adding random infos
        this.scene.run(SceneId.INFO_LAYER_SCENE); //, {'state': gameState});

        // Start DialogScene in sleep mode so it's ready to receive WebSocket events
        this.scene.run(SceneId.BASIC_DIALOG, { scene: this });
        this.scene.sleep(SceneId.BASIC_DIALOG);
    }

    public setCamera() {
        const width = this.currentMap.map.widthInPixels;
        const height = this.currentMap.map.heightInPixels;
        this.cameraService.setDefaultCamera(width, height, this.mapService.playerSprite(), this);
    }

    public initAnims() {
        // Get animation data from main character's sprite data
        const gameState = this.cache.json.get("gameState") as GameStateDTO;
        const spriteData = gameState?.main_character_sprite_data;

        if (!spriteData?.animations) {
            console.warn("No animation data found in character sprite data");
            this.animationsLoaded = true;
            return;
        }

        const animations = spriteData.animations;
        console.log("Loading animations from sprite data:", animations);

        // Create animations for each direction using the frame indices from sprite data
        const directions = ["up", "down", "left", "right"] as const;
        directions.forEach((direction) => {
            const frameIndices = animations[direction];
            if (frameIndices && frameIndices.length === 4) {
                this.anims.create(
                    this.createAnimation(direction, SpriteSheets.PLAYER, frameIndices)
                );
            } else {
                console.warn(`Invalid animation data for direction: ${direction}`);
            }
        });

        this.animationsLoaded = true;
    }

    public createAnimation(
        key: string,
        spritesheetKey: string,
        frames: number[]
    ): Phaser.Types.Animations.Animation {
        return {
            key: key,
            frames: this.anims.generateFrameNumbers(spritesheetKey, { frames: frames }),
            frameRate: 10,
            repeat: -1,
        } as Phaser.Types.Animations.Animation;
    }

    public setWarps() {
        const warps = this.currentMap.findWarps();

        warps.forEach((warp: WarpPoint) => {
            this._createWarp(warp);
        });
    }

    public _createWarp(warp: WarpPoint) {
        const uiWarp = this.physics.add.group({ classType: Phaser.GameObjects.Zone });
        if (!warp.targetWarpId) {
            console.warn("Warp point ", warp.name, " does not have a target warp ID");
            return;
        }
        //@ts-ignore
        uiWarp.create(warp.centerX(), warp.centerY(), warp.width(), warp.height());
        //@ts-ignore
        this.physics.add.overlap(
            this.currentMap.player,
            uiWarp,
            () => this.onWarpWalk(warp.targetSceneId, warp.targetWarpId),
            //@ts-ignore
            false,
            this
        );
    }

    public onWarpWalk(targetScene, targetWarpId) {
        this.unbindKeyboardEvents();
        this.unbindSocketEvents();

        console.log("warping to scene ", targetScene);
        console.log("warping stopping scene  ", this.scene);
        console.log("warping to warp point ID ", targetWarpId);

        // refacto to more or less this to remove dependencies to other map files
        //        this.scene.stop();
        if (!this.scene.get(targetScene)) {
            this.scene.add(targetScene, new AbstractMapScene(targetScene), true, {
                sceneId: targetScene,
                spawn: targetWarpId,
            });
        } else {
            this.scene.stop();
            this.scene.run(targetScene, { spawn: targetWarpId } as SceneParameters);
        }

        //        this.scene.add(targetScene, this, false, {sceneId: targetScene, spawn: warpTo});
        //        this.scene.stop();
        //        this.scene.run(targetScene, {spawn: warpTo} as SceneParameters);
    }

    public wake() {
        console.log("waking map ", this.sceneId, " !!");
        SimpleGame.CURRENT_MAP_ID = this.sceneId;
        this.cursors.left.reset();
        this.sceneParameters = null;
        this.cursors.right.reset();
        this.cursors.up.reset();
        this.cursors.down.reset();
    }

    // the logic of trigger the battle should move to the backend
    public onMeetEnemy(player, zone): void {
        // we move the zone to some other location
        zone.x = Phaser.Math.RND.between(0, this.physics.world.bounds.width);
        zone.y = Phaser.Math.RND.between(0, this.physics.world.bounds.height);

        // shake the world
        this.cameras.main.shake(300);

        this.input.stopPropagation();
        console.log("starting battle!");
        this.unbindKeyboardEvents();

        this.scene.start(SceneId.BATTLE_SCENE, {
            origin: this.sceneId,
            assets: this.currentScene.assets,
        } as BattleContextParams);
    }

    public bindKeyboardEvents() {
        this.input.keyboard.on("keydown-" + this.controls?.actionKey, () =>
            this.emitPlayerAction()
        );
        this.input.keyboard.on("keydown-" + this.controls?.cancelKey, () =>
            this.emitPlayerCancel()
        );
        this.input.keyboard.on("keydown-" + this.controls?.menuKey, () => this.openMenu());
        this.input.keyboard.on("keydown-I", () => this.openInventory());
    }

    public unbindKeyboardEvents() {
        this.input.keyboard.off("keydown-" + this.controls?.actionKey);
        this.input.keyboard.off("keydown-" + this.controls?.cancelKey);
        this.input.keyboard.off("keydown-" + this.controls?.menuKey);
        // Passing a new lambda to off() never matches, so call without handler to remove all listeners.
        this.input.keyboard.off("keydown-I");
    }

    public emitPlayerAction() {
        console.log("emitting player action from scene: ", this.sceneId);

        // SECURITY: Backend reads position and direction from server-side GameState
        // This prevents security vulnerabilities where clients could fake position data
        // Position is kept in sync via saveCharacterPosition() every 2 seconds (line 209-210)

        console.log("Sending player_action (no params - server authoritative)");
        this.eventService.emit(SocketEvents.PLAYER_ACTION);
    }

    public emitPlayerCancel() {
        console.log("emitting player cancel action from scene : ", this.sceneId);

        // SECURITY: Backend reads position and direction from server-side GameState
        // Same security model as emitPlayerAction

        console.log("Sending player_cancel (no params - server authoritative)");
        this.eventService.emit(SocketEvents.PLAYER_ACTION);
    }

    public openMenu() {
        console.log("should open menu");

        this.scene.run(SceneId.MENU_SCENE, { scene: this });
        this.scene.pause();
    }

    public openInventory() {
        console.log("should open inventory");

        this.scene.run(SceneId.INVENTORY_SCENE, { scene: this });
        this.scene.pause();
    }

    public update(time, delta): void {
        if (!this.fullyLoaded) {
            return;
        }

        const previousDirection = this.gameStateService.gameState.player_direction;
        this.playerSprite.updateFromInput(this.cursors, this.playerSprite.sprite);
        this.gameStateService.gameState.game_location.scene_id = this.sceneId;
        this.gameStateService.gameState.game_location.x = this.playerSprite.sprite.x;
        this.gameStateService.gameState.game_location.y = this.playerSprite.sprite.y;
        this.gameStateService.gameState.player_direction = this.playerSprite.getFacingDirection();

        // Debug: Log when direction changes
        if (previousDirection !== this.gameStateService.gameState.player_direction) {
            console.log(
                "Player direction changed:",
                previousDirection,
                "→",
                this.gameStateService.gameState.player_direction
            );
        }
        //        console.log("Player position: ", this.gameStateService.gameState.game_location)
    }

    public destroy() {
        console.log("destroying scene ", this.sceneId);
    }
}
